/* eslint-disable @typescript-eslint/no-floating-promises */
import { captureException } from '@sentry/browser';
import React, { useCallback } from 'react';
import AsyncSelect from 'react-select/async';
import { toast } from 'react-toastify';
import type { IZxyRegistration } from '../../../../common/src/domain/zxyExchange/IZxyRegistration';
import { DataExchangeTypes } from '../../../../common/src/enums/DataExchangeTypes';
import { useTranslate } from '../../mixins/translate/translate';
import { fetchGet } from '../../store/fetch';
import { openApiFetchGet, openApiFetchPost } from '../../store/openApiFetch';
import { appContext } from '../appContext';
import {
    Content,
    EditPanel,
    EditPanelButton,
    EditPanelButtons,
    EditPanelCell,
    EditPanelRow,
    EditPanelSelect,
    ISelectOption,
    Panel,
    PanelBody,
    PanelTopHeading,
    TopbarCrumbActive,
    TopbarLeftOnly,
    Tray,
} from '../elements';
import { Spinner } from '../spinners';
import { DisplayMessage } from '../utils/displayMessage';
import { useReactAsync } from '../utils/useReactAsync';

interface IAuthorizationRequest {
    authorized: boolean;
    authorizationId?: string;
    message?: string;
}

interface IAuthorizationError {
    error: boolean;
    message?: string;
}

interface ISelect {
    value: string;
    label: string;
}

interface IObject {
    id: string;
    code: string;
    name: string;
    location?: string;
    usage?: string;
    inspectionStandardType?: string;
    objectTypeName?: string;
    status?: string;
    batches: any[];
}

// eslint-disable-next-line @typescript-eslint/naming-convention
export function ZxyDataExchange() {
    const translate = useTranslate();
    const currentAppContext = React.useContext(appContext.context);
    const [isError, setIsError] = React.useState<IAuthorizationError>({ error: false });
    const [registrationId, setRegistrationId] = React.useState<undefined | string>();
    const [targetObjectId, setTargetObjectId] = React.useState<undefined | string>();
    const [sourceObjectId, setSourceObjectId] = React.useState<undefined | string>();
    const [objectTypes, setObjectTypes] = React.useState<ISelectOption[]>([]);
    const [objectTypeId, setObjectTypeId] = React.useState<undefined | string>();
    const [surveys, setSurveys] = React.useState<ISelectOption[]>([]);
    const [surveyId, setSurveyId] = React.useState<undefined | string>();
    const [ownerCompanies, setOwnerCompanies] = React.useState<ISelectOption[]>([]);
    const [ownerCompanyId, setOwnerCompanyId] = React.useState<undefined | string>();
    const [authorizationRequestDataExchange, setAuthorizationRequestDataExchange] = React.useState<undefined | IAuthorizationRequest>();
    const [authorizationRequestObjectList, setAuthorizationRequestObjectList] = React.useState<undefined | IAuthorizationRequest>();
    const [dataExchangeType, setDataExchangeType] = React.useState<undefined | string>(undefined);

    const loadRegistrations = useCallback(async () => {
        try {
            const result = await openApiFetchGet('/api/registrations', {});
            return result;
        } catch {
            setIsError({ error: true, message: 'ZXY Data Exchange registration server is not responding or error loading registrations.' });
            return undefined;
        }
    }, []);

    const loadHostRegistration = useCallback(async () => {
        try {
            const result = await openApiFetchGet('/api/registrationsClient', {});
            return result;
        } catch {
            setIsError({ error: true, message: 'Server is not registered or ZXY Data Exchange registration server is not responding ' });
            return undefined;
        }
    }, []);

    const { data, isLoading } = useReactAsync(loadRegistrations, {}, []);
    const hostRegistration = useReactAsync(loadHostRegistration, {}, []).data;

    let registrationList: { value: string; text: string }[] = [];

    async function startDataExchange(action: string, authorizationRequestId?: string, objectIdTarget?: string, objectIdSource?: string) {
        if (authorizationRequestId && action) {
            try {
                const result = await openApiFetchPost(
                    '/api/request/{authorizationRequestId}',
                    { authorizationRequestId },
                    {
                        targetObjectId: objectIdTarget,
                        sourceObjectId: objectIdSource,
                        action: action as DataExchangeTypes,
                        objectTypeId,
                        companyOwnerId: ownerCompanyId,
                        surveyId,
                    },
                );
                if (!result.isSuccessful) {
                    setIsError({ error: true, message: `Error data exchange: ${result.message}` });
                } else {
                    toast.info('Data exchange successful', { autoClose: 3000 });
                }
            } catch (error) {
                captureException(error);
                setIsError({ error: true, message: `Error data exchange: ${error.message}` });
            }
        } else {
            setIsError({ error: true, message: 'No authorization or unknown data exchange request' });
        }
    }

    const zxyDataExchangeTypesList = [
        {
            value: DataExchangeTypes.ReadObjectData,
            text: 'Import object from selected environment',
            objectSelectionTarget: true,
            objectSelectionSource: false,
            surveySelectionSource: false,
        },
        {
            value: DataExchangeTypes.AddObject,
            text: 'Add object in selected environment',
            objectSelectionTarget: false,
            objectSelectionSource: true,
            surveySelectionSource: false,
        },
        {
            value: DataExchangeTypes.AddSurvey,
            text: 'Add inspection to object in selected environmentt',
            objectSelectionTarget: true,
            objectSelectionSource: true,
            surveySelectionSource: true,
        },
    ];

    if (data && hostRegistration) {
        registrationList = data
            .filter((item: IZxyRegistration) => item.id !== hostRegistration.id)
            .map((item: IZxyRegistration) => ({ value: item.id, text: item.name }));
    }

    React.useEffect(() => {
        setIsError({ error: false, message: '' });
        if (registrationId && dataExchangeType && hostRegistration) {
            (async () => {
                const requestDataExchange = await openApiFetchPost(
                    '/api/registrations/requestAuthorization',
                    {},
                    {
                        key: hostRegistration.secretKey,
                        recipientId: registrationId,
                        requestType: dataExchangeType,
                    },
                );
                const requestReadObjectList = await openApiFetchPost(
                    '/api/registrations/requestAuthorization',
                    {},
                    {
                        key: hostRegistration.secretKey,
                        recipientId: registrationId,
                        requestType: DataExchangeTypes.ReadObjectList,
                    },
                );

                if (!requestDataExchange.authorized) {
                    setIsError({ error: true, message: 'ZXY Registration server is not responding or error loading data' });
                }
                setAuthorizationRequestDataExchange(requestDataExchange);
                setAuthorizationRequestObjectList(requestReadObjectList);
            })();
        }
    }, [registrationId, dataExchangeType, hostRegistration]);

    React.useEffect(() => {
        if (dataExchangeType && dataExchangeType === DataExchangeTypes.ReadObjectData) {
            (async () => {
                const responseObjectTypes = await fetchGet(`/api/objectTypesList?languageCode=${currentAppContext.languageCode}`);
                setObjectTypes(
                    responseObjectTypes.json.map((objectType: { id: string; name: string }) => ({ text: objectType.name, value: objectType.id })),
                );

                const responseOwnerCompanies = await fetchGet('/api/companyList?type=owner', {}, ['NO_DATA_AVAILABLE']);
                setOwnerCompanies(
                    responseOwnerCompanies.json.items.map((company: { id: string; name: string }) => ({ text: company.name, value: company.id })),
                );
            })();
        }
    }, [dataExchangeType, currentAppContext.languageCode]);

    React.useEffect(() => {
        if (dataExchangeType && dataExchangeType === DataExchangeTypes.AddSurvey) {
            (async () => {
                const responseSurveys = await fetchGet('/api/surveys', { objectId: sourceObjectId });
                setSurveys(
                    responseSurveys.json.items.map((survey: { id: string; description: string }) => ({ text: survey.description, value: survey.id })),
                );
            })();
        }
    }, [dataExchangeType, sourceObjectId]);

    const selectStyles = {
        menu: (styles: any) => ({ ...styles, zIndex: 999 }),
    };

    const loadAvailableObjectsTarget = async (input?: string) => {
        let options: ISelect[] = [];

        if (input && input.length >= 4 && authorizationRequestObjectList?.authorizationId) {
            try {
                const result = await openApiFetchGet('/api/request/objects/{authorizationRequestId}', {
                    authorizationRequestId: authorizationRequestObjectList.authorizationId,
                    name: input,
                });

                options = result.map((object) => ({ value: object.id, label: object.name }));
                return options;
            } catch (error) {
                captureException(error);
                setIsError({ error: true, message: 'Error loading objects in target environment' });
            }
        }
        return undefined;
    };

    const loadAvailableObjectsSource = async (input?: string) => {
        let options: ISelect[] = [];

        if (input && input.length >= 3) {
            const url = '/api/objects';
            const result = await fetchGet(url, { objectName: input }, ['NO_DATA_AVAILABLE']);
            options = result.json.items
                .filter((object: IObject) => object.code !== null)
                .map((object: IObject) => ({ value: object.id, label: object.name }));
        }
        return options;
    };

    const isObjectSourceSelectionNeeded =
        dataExchangeType && zxyDataExchangeTypesList.find((item) => item.value === dataExchangeType)?.objectSelectionSource;
    const isObjectTargetSelectionNeeded =
        dataExchangeType && zxyDataExchangeTypesList.find((item) => item.value === dataExchangeType)?.objectSelectionTarget;
    const isSurveySelectionNeeded =
        dataExchangeType && zxyDataExchangeTypesList.find((item) => item.value === dataExchangeType)?.surveySelectionSource;

    return (
        <>
            <TopbarLeftOnly>
                <TopbarCrumbActive title="ZXY Exchange" icon="fa-exchange" />
            </TopbarLeftOnly>
            <Content>
                <Tray>
                    {isError && <DisplayMessage message={isError.message} messageType="error" />}
                    <Panel topBorder={true}>
                        {(!isError && isLoading && <Spinner />) || (
                            <>
                                <PanelTopHeading>Transfer data between systems (beta)</PanelTopHeading>
                                <PanelBody>
                                    <EditPanel>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSelect
                                                    name="registrationId"
                                                    placeholder="Select environment"
                                                    value={registrationId}
                                                    options={registrationList}
                                                    handleChange={(value) => setRegistrationId(value)}
                                                    topLabel="Environment"
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        <EditPanelRow>
                                            <EditPanelCell rowCount={6}>
                                                <EditPanelSelect
                                                    name="dataExchangeType"
                                                    placeholder="Select data transfer"
                                                    value={dataExchangeType}
                                                    options={zxyDataExchangeTypesList}
                                                    handleChange={(value) => setDataExchangeType(value)}
                                                    topLabel="Data that should be transferred"
                                                />
                                            </EditPanelCell>
                                        </EditPanelRow>
                                        {isObjectTargetSelectionNeeded && (
                                            <>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <div className="mb10">
                                                            Select object in environment:{' '}
                                                            {registrationList.find((item) => item.value === registrationId)?.text}
                                                        </div>
                                                        {(dataExchangeType && authorizationRequestObjectList?.authorized && (
                                                            <div style={{ width: '460px' }}>
                                                                <AsyncSelect
                                                                    id="targetObject"
                                                                    name="targetObject"
                                                                    backspaceRemoves={true}
                                                                    isClearable={true}
                                                                    multi={false}
                                                                    styles={selectStyles}
                                                                    inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'off' }}
                                                                    valueKey="value"
                                                                    labelKey="label"
                                                                    loadOptions={async (inputValue: any) => loadAvailableObjectsTarget(inputValue)}
                                                                    onChange={(option: any) => {
                                                                        if (option) {
                                                                            setTargetObjectId(option.value);
                                                                        }
                                                                    }}
                                                                    placeholder="Search object"
                                                                />
                                                            </div>
                                                        )) || <div>Not authorized to see list of object in selected environment.</div>}
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                                {targetObjectId && dataExchangeType === DataExchangeTypes.ReadObjectData && (
                                                    <EditPanelRow>
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelSelect
                                                                name="objectTypeId"
                                                                placeholder={translate('common.selectItem', translate('common.objectTypes'))}
                                                                value={objectTypeId}
                                                                options={objectTypes}
                                                                handleChange={(value) => setObjectTypeId(value)}
                                                                topLabel="Select type for added object"
                                                            />
                                                        </EditPanelCell>
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelSelect
                                                                name="ownerCompanyId"
                                                                placeholder={translate('common.selectItem', translate('common.owner'))}
                                                                value={ownerCompanyId}
                                                                options={ownerCompanies}
                                                                handleChange={(value) => setOwnerCompanyId(value)}
                                                                topLabel="Select owner for added object"
                                                            />
                                                        </EditPanelCell>
                                                    </EditPanelRow>
                                                )}
                                            </>
                                        )}
                                        {isObjectSourceSelectionNeeded && (
                                            <>
                                                <EditPanelRow>
                                                    <EditPanelCell rowCount={6}>
                                                        <div className="mb10">Select object in own environment</div>
                                                        <div style={{ width: '460px' }}>
                                                            <AsyncSelect
                                                                id="sourceObject"
                                                                name="sourceObject"
                                                                backspaceRemoves={true}
                                                                isClearable={true}
                                                                multi={false}
                                                                styles={selectStyles}
                                                                inputProps={{ autoComplete: 'off', autoCorrect: 'off', spellCheck: 'off' }}
                                                                valueKey="value"
                                                                labelKey="label"
                                                                loadOptions={async (inputValue: any) => loadAvailableObjectsSource(inputValue)}
                                                                onChange={(option: any) => {
                                                                    if (option) {
                                                                        setSourceObjectId(option.value);
                                                                    }
                                                                }}
                                                                placeholder="Search object"
                                                            />
                                                        </div>
                                                    </EditPanelCell>
                                                </EditPanelRow>
                                                {isSurveySelectionNeeded && (
                                                    <EditPanelRow>
                                                        <EditPanelCell rowCount={6}>
                                                            <EditPanelSelect
                                                                name="surveyId"
                                                                placeholder="Select survey"
                                                                value={surveyId}
                                                                options={surveys}
                                                                handleChange={(value) => setSurveyId(value)}
                                                                topLabel="Inspection"
                                                            />
                                                        </EditPanelCell>
                                                    </EditPanelRow>
                                                )}
                                            </>
                                        )}
                                    </EditPanel>
                                </PanelBody>
                                {authorizationRequestDataExchange?.authorizationId && dataExchangeType && (
                                    <EditPanelButtons footer={true}>
                                        <EditPanelButton
                                            onClick={async () =>
                                                startDataExchange(
                                                    dataExchangeType,
                                                    authorizationRequestDataExchange?.authorizationId,
                                                    targetObjectId,
                                                    sourceObjectId,
                                                )
                                            }
                                            text="Start data transfer"
                                            small={false}
                                            primary={true}
                                        />
                                    </EditPanelButtons>
                                )}
                            </>
                        )}
                    </Panel>
                </Tray>
            </Content>
        </>
    );
}
